import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { createIssue } from '../api/issueApi';
import CompactStoryForm from './CompactStoryForm';
import MentionComposer from './MentionComposer';
import { issueFormModalPanelStyle } from '../constants/issueFormModal';
import { resolveAssigneeIdsFromText } from '../utils/dailyNotebookMentions';
import { normalizeProjectMembersResponse } from '../utils/assigneeTeamFilter';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import { COLORS as C, RADIUS } from '../constants/ui';

const modalOverlay = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15,23,42,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '1rem',
};

/**
 * The one place work items are created from Sprint Board and My Tasks.
 *
 * Two steps, because that matches how the work is actually planned: name the
 * Story, then list what it takes. Step two is the same "title @person + Enter"
 * row as the Story detail modal, so a Story with three tasks is one modal
 * instead of four trips.
 *
 * Step two only appears for a Story — a Task has no children to add. Closing
 * after step one is perfectly valid; the Story already exists by then.
 */
export default function WorkItemCreateModal({
  open,
  onClose,
  projectId,
  sprintId,
  sprintsEnabled = true,
  preferAssignedProjects = false,
  defaultEpicId = null,
  onCreated,
}) {
  const qc = useQueryClient();
  useBodyScrollLock(open);
  const [createdStory, setCreatedStory] = useState(null);
  const [addedTasks, setAddedTasks] = useState([]);
  const [taskText, setTaskText] = useState('');

  const storyProjectId = createdStory?.projectid || projectId;

  const { data: membersRaw } = useQuery({
    queryKey: ['project-members', storyProjectId],
    queryFn: () => api.get(`/projects/${storyProjectId}/members`).then((r) => r.data),
    enabled: open && !!storyProjectId,
  });
  const members = normalizeProjectMembersResponse(membersRaw);

  const close = () => {
    setCreatedStory(null);
    setAddedTasks([]);
    setTaskText('');
    onClose?.();
  };

  const quickAdd = useMutation({
    mutationFn: async (raw) => {
      // Every task must have an owner — same rule as the Daily Notebook.
      if (!raw.includes('@')) {
        throw new Error('Tag who owns this task with @');
      }
      const assigneeIds = resolveAssigneeIdsFromText(raw, members);
      if (assigneeIds.length === 0) {
        throw new Error('Could not match @name to a project member — pick from the @ list');
      }
      const assigneeId = assigneeIds[0] ?? null;
      const member = members.find((m) => Number(m.id) === Number(assigneeId));
      const res = await createIssue({
        projectid: createdStory.projectid || projectId,
        sprint_id: createdStory.sprint_id ?? sprintId ?? null,
        issuetype: 'Task',
        description: raw,
        assignee_id: assigneeId,
        assigneeteam: member?.team || createdStory.assigneeteam || 'Any',
        priority: createdStory.priority || 'High',
        parent_id: createdStory.issueid,
      });
      return { issue: res?.data?.issue ?? res?.data, memberName: member?.full_name };
    },
    onSuccess: ({ issue, memberName }) => {
      setAddedTasks((prev) => [...prev, { ...issue, _assigneeName: memberName }]);
      setTaskText('');
      qc.invalidateQueries({ queryKey: ['board'] });
      qc.invalidateQueries({ queryKey: ['my-issues'] });
      qc.invalidateQueries({ queryKey: ['story-subtasks', createdStory.issueid] });
      onCreated?.();
      toast.success(memberName ? `Task added for ${memberName}` : 'Task added');
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || err.message || 'Failed to add task'),
  });

  const handleQuickAdd = () => {
    const raw = taskText.trim();
    if (!raw || quickAdd.isPending) return;
    quickAdd.mutate(raw);
  };

  if (!open) return null;

  const storyTitle = (createdStory?.description || '').split('\n')[0]?.trim();

  return (
    <div style={modalOverlay} role="presentation" onClick={close}>
      <div
        style={issueFormModalPanelStyle}
        role="dialog"
        aria-modal="true"
        aria-label={createdStory ? 'Add tasks to the new story' : 'New work item'}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: C.ink, fontWeight: 800 }}>
            {createdStory ? 'Add tasks' : 'New work item'}
          </h3>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.inkFaint }}
          >
            ✕
          </button>
        </div>

        {!createdStory ? (
          <CompactStoryForm
            sprintId={sprintId || null}
            projectId={projectId || null}
            sprintsEnabled={sprintsEnabled}
            preferAssignedProjects={preferAssignedProjects}
            defaultEpicId={defaultEpicId}
            onCreated={(issue) => {
              onCreated?.(issue);
              setCreatedStory(issue);
              toast.success('Story created — add its tasks below');
            }}
          />
        ) : (
          <div>
            <div
              style={{
                padding: '10px 12px',
                borderRadius: RADIUS.control,
                background: C.doneSoft,
                color: C.done,
                fontSize: '0.85rem',
                fontWeight: 700,
                marginBottom: 14,
              }}
            >
              ✓ Story #{createdStory.issueid} created
              {storyTitle ? <span style={{ fontWeight: 500 }}> — {storyTitle}</span> : null}
            </div>

            <MentionComposer
              value={taskText}
              onChange={setTaskText}
              onSubmit={handleQuickAdd}
              members={members}
              disabled={quickAdd.isPending}
              placeholder="Add a task… type @ to assign, Enter to add"
            />
            <div style={{ fontSize: '0.68rem', color: C.inkFaint, margin: '4px 0 12px' }}>
              {quickAdd.isPending ? 'Adding task…' : 'Enter to add · @ to assign · add as many as you need'}
            </div>

            {addedTasks.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                {addedTasks.map((t) => (
                  <div
                    key={t.issueid}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '7px 10px',
                      borderRadius: RADIUS.control,
                      border: `1px solid ${C.line}`,
                      background: '#fff',
                      fontSize: '0.85rem',
                    }}
                  >
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: C.accent }}>
                      #{t.issueid}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, color: C.ink, overflowWrap: 'anywhere' }}>
                      {(t.description || '').split('\n')[0]}
                    </span>
                    {t._assigneeName && (
                      <span style={{ fontSize: '0.72rem', color: C.inkSoft, whiteSpace: 'nowrap' }}>
                        {t._assigneeName}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={close}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: RADIUS.control,
                border: 'none',
                background: C.accentInk,
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              {addedTasks.length > 0
                ? `Done — ${addedTasks.length} task${addedTasks.length > 1 ? 's' : ''} added`
                : 'Done'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
